
import { NomineeDiscoveryItem, Asset, Allocation } from "../types";
import { supabase } from "./authService";

export const nomineeService = {
  /**
   * Discovers all estate inheritances for a given nominee email.
   * Scans the normalized 'nominees' table to find owner contexts,
   * then fetches and filters assets for those owners.
   */
  async discoverInheritances(nomineeEmail: string): Promise<NomineeDiscoveryItem[]> {
    const results: NomineeDiscoveryItem[] = [];
    const emailLower = nomineeEmail.toLowerCase();

    try {
      // 1. Find all instances where this email is registered as a nominee
      const { data: nomineeRecords, error: nomineeError } = await supabase
        .from('nominees')
        .select('*')
        .eq('email', emailLower);

      if (nomineeError) throw nomineeError;
      if (!nomineeRecords || nomineeRecords.length === 0) return [];

      // 2. Process each owner context (A nominee might have inheritances from multiple owners)
      for (const nomineeRow of nomineeRecords) {
        const ownerId = nomineeRow.nominator_id;
        const nomineeIdInOwnerContext = nomineeRow.id;

        // Fetch owner's basic information (Name)
        const { data: ownerInfo, error: ownerError } = await supabase
          .from('user_information')
          .select('full_name')
          .eq('user_id', ownerId)
          .single();

        if (ownerError) continue;

        // Fetch all assets belonging to this owner
        const { data: ownerAssets, error: assetError } = await supabase
          .from('assets')
          .select('*')
          .eq('user_id', ownerId);

        if (assetError || !ownerAssets) continue;

        // Filter assets where THIS nominee is tagged in the allocations
        const taggedAssets = ownerAssets
          .filter(asset => {
            const allocations = (asset.allocations || []) as Allocation[];
            return allocations.some(al => al.personId === nomineeIdInOwnerContext);
          })
          .map(asset => {
            const allocations = (asset.allocations || []) as Allocation[];
            const myAllocation = allocations.find(al => al.personId === nomineeIdInOwnerContext)!;
            
            return {
              id: asset.id,
              name: asset.name,
              category: asset.category,
              value: Number(asset.value),
              institution: asset.institution,
              customerId: asset.identifier_value,
              branchDetails: asset.location_details,
              claimPath: asset.claim_path,
              contactAuthority: asset.contact_authority,
              status: asset.status,
              allocations: allocations,
              nomineePercentage: myAllocation.percentage,
              nomineeEstimatedValue: (Number(asset.value) * myAllocation.percentage) / 100,
              nomineeRole: myAllocation.nomineeRole,
              nomineeType: myAllocation.nomineeType
            };
          });

        if (taggedAssets.length > 0) {
          results.push({
            ownerName: ownerInfo.full_name,
            ownerId: ownerId,
            relationship: nomineeRow.relationship,
            totalValueForNominee: taggedAssets.reduce((sum, a) => sum + a.nomineeEstimatedValue, 0),
            assets: taggedAssets as any // Type assertion to match NomineeDiscoveryItem expectation
          });
        }
      }
    } catch (e) {
      console.error("Discovery error:", e);
    }

    return results;
  }
};
